import {
  ArrowDown, Buildings, IdentificationCard, MapPin, Bank, PaperPlaneTilt,
  CheckCircle, Rocket, Envelope, UserCheck, SignIn, Info, Shield,
} from "@phosphor-icons/react";

const PHASE_A_STEPS = [
  {
    number: 1,
    title: "Start the Request",
    icon: Buildings,
    path: "Purchasing Strategy \u2192 Supplier Master \u2192 Request New Supplier",
    description: "Two ways to start: fill the form yourself, or click \"Send Registration Link to Supplier\" and enter their email - they fill in their own details through a link, and you confirm it's genuinely them by entering a PIN code they read back to you.",
  },
  {
    number: 2,
    title: "Company & Compliance Details",
    icon: IdentificationCard,
    path: "Company Details section",
    description: "Company/Supplier Name, whether they're GST-registered, and a Category. If GST-registered, enter the GSTIN and click Verify to confirm it against government records. If not GST-registered, a PAN is required instead.",
  },
  {
    number: 3,
    title: "Contact & Address",
    icon: MapPin,
    path: "Primary Contact + Registered Address sections",
    description: "A Primary Contact (name, email, 10-digit mobile) and a Secondary Contact (name, 10-digit mobile) are both mandatory - plus the registered street address, city, state and PIN code.",
  },
  {
    number: 4,
    title: "Bank Details (mandatory, finance reference only)",
    icon: Bank,
    path: "Bank Details section",
    description: "Uploading a cancelled cheque is required - the account number, IFSC and account holder name are read automatically from it (or can be corrected by hand afterwards). Tick the confirmation checkbox stating you've reviewed them before submitting - these stay as a reference note only for now, Finance enters them directly in SAP when needed.",
  },
  {
    number: 5,
    title: "Attach Documents & Submit",
    icon: PaperPlaneTilt,
    path: "Supporting Attachments section",
    description: "Attach the GST certificate, PAN card, cancelled cheque, or anything else relevant, then click Submit for Approval. The request moves to \"Pending Admin Review\".",
  },
  {
    number: 6,
    title: "Admin Reviews & Approves",
    icon: CheckCircle,
    path: "Admin \u2192 New Supplier Approval",
    description: "An admin opens the request, reviews the details and attachments, then either Approves it - which creates the supplier in SAP as \"In Preparation\" - or Rejects it with a reason. A rejected request never touches SAP.",
  },
  {
    number: 7,
    title: "Finance Activates in SAP",
    icon: Rocket,
    path: "Admin \u2192 New Supplier Approval \u2192 approved tab",
    description: "A separate Finance user ticks \"Credit check & tax compliance verified\", then clicks Activate in SAP - this flips the supplier from \"In Preparation\" to \"Active\" in SAP, so it can finally be used on purchase orders.",
  },
];

const PHASE_B_STEPS = [
  {
    number: 1,
    title: "Send a Portal Invite",
    icon: Envelope,
    path: "Admin \u2192 Supplier Portal Invite",
    description: "Needs the Vendor Code created in Phase A. Enter the company name, vendor code and the supplier contact's email - they'll receive a signup link by email.",
  },
  {
    number: 2,
    title: "Supplier Self-Registers",
    icon: UserCheck,
    path: "Supplier's own signup link",
    description: "The supplier enters their Vendor Code, Company Name, Email and a password, plus their GST Number and PAN Number with the certificates uploaded. The account is created as \"pending\".",
  },
  {
    number: 3,
    title: "Staff Approves the Portal Account",
    icon: Shield,
    path: "Admin \u2192 Supplier Portal Approvals",
    description: "Staff review the pending signup and either Approve or Reject it (with a reason) - separate from the SAP creation/activation approvals in Phase A.",
  },
  {
    number: 4,
    title: "Supplier Logs In",
    icon: SignIn,
    path: "Supplier Portal login page",
    description: "Once approved, the supplier can log in, see their own SAP purchase orders, and submit shipments/GRNs electronically.",
  },
];

function StepList({ steps, testPrefix }) {
  return (
    <ol className="space-y-6">
      {steps.map((step, idx) => {
        const Icon = step.icon;
        return (
          <li key={step.number} className="relative" data-testid={`${testPrefix}-step-${step.number}`}>
            <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden shadow-sm">
              <div className="flex items-center gap-3 px-5 sm:px-6 py-4 border-b border-[#EAECF0] bg-[#F8FAFC]">
                <div className="w-9 h-9 rounded-full bg-[#004B87] text-white font-heading font-bold text-sm flex items-center justify-center shrink-0" data-testid={`${testPrefix}-step-${step.number}-number`}>
                  {step.number}
                </div>
                <Icon size={18} className="text-[#004B87] shrink-0" />
                <h2 className="font-heading text-base sm:text-lg font-semibold text-[#101828] tracking-tight" data-testid={`${testPrefix}-step-${step.number}-title`}>
                  {step.title}
                </h2>
              </div>
              <div className="p-5 sm:p-6">
                <p className="font-data text-[11px] font-bold text-[#0E7C86] uppercase tracking-wide mb-2" data-testid={`${testPrefix}-step-${step.number}-path`}>
                  {step.path}
                </p>
                <p className="text-sm text-[#475569] leading-relaxed" data-testid={`${testPrefix}-step-${step.number}-description`}>
                  {step.description}
                </p>
              </div>
            </div>
            {idx < steps.length - 1 && (
              <div className="flex justify-center py-1.5">
                <ArrowDown size={18} className="text-[#94A3B8]" />
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
}

export default function SupplierCreationInstructionsPage() {
  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="supplier-creation-instructions-page">
      <header className="min-h-16 bg-[#0E7C86] shadow-[0_1px_3px_0_rgba(16,24,40,0.15)] flex items-center px-4 sm:px-6 shrink-0 z-10">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-md bg-white/15 flex items-center justify-center">
            <Buildings size={18} className="text-white" weight="fill" />
          </div>
          <span className="font-heading text-[16px] font-bold text-white tracking-tight">Purchasing Strategy - Work Instruction</span>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="mb-10">
          <p className="font-data text-xs font-bold text-[#004B87] uppercase tracking-widest mb-2" data-testid="wi-doc-code">SOP-SUP-01 &middot; Supplier Master</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#101828] tracking-tight" data-testid="wi-page-title">
            How to Create a New Supplier
          </h1>
          <p className="text-base text-[#475569] mt-3 max-w-2xl leading-relaxed">
            The process has two phases: creating the supplier in SAP (required for every new supplier), and optionally
            granting them Supplier Portal access so they can submit their own shipments electronically.
          </p>
        </div>

        <div className="flex items-start gap-3 bg-[#EFF6FF] border border-[#BFDBFE] rounded-lg p-4 mb-10" data-testid="wi-info-banner">
          <Info size={20} className="text-[#1E40AF] shrink-0 mt-0.5" weight="fill" />
          <p className="text-sm text-[#1E40AF] leading-relaxed">
            Approval (Phase A, step 6) and Activation (Phase A, step 7) are always two separate steps, each gated
            behind its own permission - a supplier created in SAP as "In Preparation" cannot be used on a purchase
            order until it's activated. In practice, this is normally done by an admin and a finance user
            respectively, since each needs a different permission.
          </p>
        </div>

        <div className="mb-6">
          <p className="font-data text-xs font-bold text-[#0E7C86] uppercase tracking-widest" data-testid="wi-phase-a-label">Phase A &middot; Required for every new supplier</p>
          <h2 className="font-heading text-xl font-bold text-[#101828] mt-1">Create the Supplier in SAP</h2>
        </div>
        <StepList steps={PHASE_A_STEPS} testPrefix="wi-phase-a" />

        <div className="mt-16 mb-6">
          <p className="font-data text-xs font-bold text-[#0E7C86] uppercase tracking-widest" data-testid="wi-phase-b-label">Phase B &middot; Optional - only if the supplier will submit shipments electronically</p>
          <h2 className="font-heading text-xl font-bold text-[#101828] mt-1">Give the Supplier Portal Access</h2>
        </div>
        <StepList steps={PHASE_B_STEPS} testPrefix="wi-phase-b" />

        <div className="mt-12 border-t border-[#EAECF0] pt-6 text-center">
          <p className="text-xs text-[#94A3B8] font-data">Purchasing Strategy Work Instruction &middot; Supplier Creation</p>
        </div>
      </main>
    </div>
  );
}
